import React from 'react';
import { Icon } from './icons';
import { TopBar } from './common';
import { InsightsResponse, AnalyticsData, BookV2 } from '../types';

export const ReportScreen: React.FC<{
  analytics: AnalyticsData | null;
  insights: InsightsResponse | null;
  loading: boolean;
  error: string | null;
  readingBooks: BookV2[];
  onBack: () => void;
  onRefresh: () => void;
  onApplyGoalForBook?: (bookId: string, pages: number) => void | Promise<void>;
  onApplyAllGoals?: (goals: { bookId: string; pages: number }[]) => void | Promise<void>;
}> = ({ analytics, insights, loading, error, readingBooks, onBack, onRefresh, onApplyGoalForBook, onApplyAllGoals }) => {
  const a = analytics;
  return (
    <div className="scroll" style={{ paddingBottom: 120 }}>
      <TopBar onBack={onBack} title="Relatório" trailing={
        <button onClick={onRefresh} style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'var(--paper-2)', border: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><Icon name="sparkle" size={16} color="var(--ember)"/></button>
      }/>

      <div style={{ padding: '16px 20px 0' }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ember)' }}>
          <Icon name="sparkle" size={11} color="var(--ember)"/> Análise inteligente · {new Date().toLocaleDateString('pt-BR', { month: 'long', day: 'numeric' })}
        </div>
        <div className="serif" style={{ fontSize: 34, lineHeight: 1.05, marginTop: 10, letterSpacing: '-0.01em' }}>
          O que seus dados<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>contam sobre você</span>.
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, maxWidth: 300 }}>
          Cada página registrada vira conhecimento sobre o seu ritmo. Use-o para criar hábito — não culpa.
        </div>
      </div>

      <div style={{ padding: '24px 20px 0', display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 10 }}>
        <MetricTile big label="Média / dia" value={String(a?.avgPerDay ?? 0)} suffix="pág" hint={`em ${a?.consistency ?? 0} dos últimos 30 dias`} accent="ember"/>
        <MetricTile label="Sessão média" value={String(a?.avgPerSession ?? 0)} suffix="pág"/>
        <MetricTile label="Melhor" value={String(a?.longestSession ?? 0)} suffix="pág" hint="numa só vez"/>
      </div>

      <div className="section-head">
        <div>
          <div className="label">Quando você lê</div>
          <div className="serif title" style={{ marginTop: 4 }}>Seu relógio de leitura</div>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        <div className="card" style={{ padding: 20 }}>
          {a?.hours && a.hours.length > 0 ? <>
            <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 90 }}>
              {a.hours.map((v, i) => {
                const max = Math.max(...a.hours, 1);
                const h = v > 0 ? Math.max(4, (v / max) * 80) : 2;
                const isPeak = i === a.bestHour;
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                    <div style={{
                      width: '100%', height: h, borderRadius: 2,
                      background: isPeak ? 'var(--ember)' : (v > 0 ? 'var(--ember-soft)' : 'var(--paper-3)'),
                    }}/>
                  </div>
                );
              })}
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', marginTop: 8,
              fontSize: 9, color: 'var(--ink-4)', fontFamily: 'Geist Mono, monospace',
            }}>
              <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>24h</span>
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: a.peakBlock === 'manhã' ? 'var(--ember-soft)' : a.peakBlock === 'noite' ? 'rgba(107,62,92,0.18)' : 'var(--sage-soft)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={a.peakBlock === 'manhã' ? 'sun' : (a.peakBlock === 'noite' || a.peakBlock === 'madrugada') ? 'moon' : 'timer'} size={18}
                  color={a.peakBlock === 'manhã' ? 'var(--ember)' : a.peakBlock === 'noite' ? 'var(--plum)' : 'var(--sage)'}/>
              </div>
              <div>
                <div className="serif" style={{ fontSize: 19, lineHeight: 1.1 }}>
                  Pico às <span style={{ color: 'var(--ember)' }}>{a.bestHour}h</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                  Você lê mais {a.peakBlock === 'madrugada' ? 'de madrugada' : `pela ${a.peakBlock}`}. Proteja esse horário.
                </div>
              </div>
            </div>
          </> : (
            <div style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, padding: 20 }}>
              Ainda sem dados suficientes para gerar o relógio.
            </div>
          )}
        </div>
      </div>

      <div className="section-head">
        <div>
          <div className="label">Dias fortes</div>
          <div className="serif title" style={{ marginTop: 4 }}>Seu calendário</div>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        <div className="card" style={{ padding: 20 }}>
          {['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'].map((label, i) => {
            const idx = i === 6 ? 0 : i + 1; // 0=Dom..6=Sáb
            const pages = a?.weekdays?.[idx] || 0;
            const max = a?.weekdays ? Math.max(1, ...a.weekdays) : 1;
            const pct = pages / max;
            const isPeak = idx === (a?.bestWeekdayIdx ?? -1) && pages > 0;
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: i < 6 ? 10 : 0 }}>
                <div className="mono" style={{ width: 30, fontSize: 10, color: isPeak ? 'var(--ember)' : 'var(--ink-3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--paper-3)', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${Math.max(pct * 100, pages > 0 ? 5 : 0)}%`,
                    background: isPeak ? 'var(--ember)' : 'var(--ember-soft)', borderRadius: 3,
                  }}/>
                </div>
                <div className="mono" style={{ width: 40, fontSize: 10, color: 'var(--ink-3)', textAlign: 'right' }}>
                  {pages}p
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="section-head">
        <div>
          <div className="label" style={{ color: 'var(--ember)' }}>
            <Icon name="sparkle" size={11} color="var(--ember)"/> Insights inteligentes
          </div>
          <div className="serif title" style={{ marginTop: 4 }}>Leitura dos seus dados</div>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        {loading && <AIInsightsSkeleton/>}
        {error && !loading && (
          <div className="card" style={{ padding: 18, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{error}</div>
            <button onClick={onRefresh} style={{
              marginTop: 10, padding: '8px 14px', borderRadius: 10,
              background: 'var(--paper-3)', fontSize: 12, color: 'var(--ink)',
            }}>Tentar novamente</button>
          </div>
        )}
        {insights && !loading && <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {insights.insights.map((ins, i) => (
              <div key={i} className="card" style={{ padding: 18, display: 'flex', gap: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                  background: 'var(--ember-soft)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><Icon name={ins.icon || 'sparkle'} size={18} color="var(--ember)"/></div>
                <div style={{ flex: 1 }}>
                  <div className="serif" style={{ fontSize: 19, lineHeight: 1.15 }}>{ins.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 4, lineHeight: 1.45 }}>{ins.body}</div>
                </div>
              </div>
            ))}
          </div>

          {insights.goalsSuggestion && insights.goalsSuggestion.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="mono" style={{
                fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: 'var(--ember)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Icon name="target" size={11} color="var(--ember)"/>
                Metas sugeridas por livro
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {insights.goalsSuggestion.map((g, i) => {
                  const book = readingBooks.find(b => b.id === g.bookId);
                  const currentGoal = book?.dailyGoal;
                  return (
                    <div key={g.bookId || i} style={{
                      padding: 18, borderRadius: 18,
                      background: 'var(--ink)', color: 'var(--paper)',
                      position: 'relative', overflow: 'hidden',
                    }}>
                      <div className="serif" style={{ fontSize: 17, lineHeight: 1.2 }}>{g.title}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8 }}>
                        <div className="serif" style={{ fontSize: 40, lineHeight: 1, color: 'var(--ember)', letterSpacing: '-0.02em' }}>{g.pages}</div>
                        <div className="serif" style={{ fontSize: 14, fontStyle: 'italic' }}>páginas / dia</div>
                        {currentGoal && currentGoal !== g.pages && (
                          <div className="mono" style={{
                            fontSize: 10, color: 'var(--ink-4)', marginLeft: 'auto',
                            letterSpacing: '0.1em', textTransform: 'uppercase',
                          }}>atual: {currentGoal}</div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 8, lineHeight: 1.4, fontStyle: 'italic' }}>
                        "{g.reason}"
                      </div>
                      {onApplyGoalForBook && (
                        <button onClick={() => onApplyGoalForBook(g.bookId, g.pages)} style={{
                          marginTop: 12, width: '100%',
                          background: 'var(--ember)', color: 'var(--paper)',
                          padding: '10px', borderRadius: 10, fontSize: 13, fontWeight: 500,
                        }}>Aplicar esta meta</button>
                      )}
                    </div>
                  );
                })}
              </div>
              {onApplyAllGoals && insights.goalsSuggestion.length > 1 && (
                <button
                  onClick={() => onApplyAllGoals(insights.goalsSuggestion.map(g => ({ bookId: g.bookId, pages: g.pages })))}
                  style={{
                    width: '100%', marginTop: 10,
                    background: 'var(--paper-2)', color: 'var(--ink)',
                    border: '1px solid var(--ember)',
                    padding: '13px', borderRadius: 12,
                    fontSize: 14, fontWeight: 500,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  <Icon name="sparkle" size={14} color="var(--ember)"/>
                  Aplicar todas as metas
                </button>
              )}
              <div style={{
                marginTop: 10, padding: '10px 14px', borderRadius: 10,
                background: 'var(--paper-2)', color: 'var(--ink-3)',
                fontSize: 11, textAlign: 'center',
                fontFamily: 'Geist Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>
                Meta do dia = {insights.goalsSuggestion.reduce((s, g) => s + g.pages, 0)} páginas
              </div>
            </div>
          )}

          {insights.encouragement && (
            <div className="serif" style={{
              marginTop: 18, padding: '0 8px',
              fontSize: 20, lineHeight: 1.3, fontStyle: 'italic',
              color: 'var(--ink-2)', textAlign: 'center',
            }}>
              "{insights.encouragement}"
            </div>
          )}

          {insights.source === 'fallback' && (
            <div style={{ fontSize: 10, textAlign: 'center', color: 'var(--ink-4)', marginTop: 12 }}>
              (insights gerados localmente — IA indisponível)
            </div>
          )}
        </>}
      </div>

      <div className="section-head">
        <div className="serif title">Dados analisados</div>
      </div>
      <div style={{ padding: '0 20px' }}>
        <div className="card" style={{ padding: 16 }}>
          {(a ? [
            ['Sessões registradas', a.totalSessions],
            ['Dias com leitura (30d)', `${a.consistency} / 30`],
            ['Páginas últimos 7 dias', a.last7],
            // É a variação contra a semana anterior; sem semana anterior não há
            // base, e "0%" parecia uma medida (pente fino, QA-23).
            ...((a.prev7 || 0) > 0 ? [['Ritmo vs. semana passada', `${a.velocity > 0 ? '+' : ''}${a.velocity}%`]] : []),
            ['Livros ativos', a.totals.readingBooks],
            ['Livros terminados', a.totals.finishedBooks],
          ] : []).map((row, i, arr) => (
            <div key={row[0]} style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--line)' : 'none',
              fontSize: 13,
            }}>
              <span style={{ color: 'var(--ink-3)' }}>{row[0]}</span>
              <span className="mono" style={{ color: 'var(--ink)' }}>{row[1]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function MetricTile({ label, value, suffix, hint, accent, big }: { label: string; value: string; suffix?: string; hint?: string; accent?: string; big?: boolean }) {
  const color = accent === 'ember' ? 'var(--ember)' : 'var(--ink)';
  return (
    <div className="card" style={{ padding: big ? 16 : 14 }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{label}</div>
      <div className="serif" style={{ fontSize: big ? 38 : 26, lineHeight: 1.05, marginTop: 6, color, letterSpacing: '-0.02em' }}>
        {value}
        {suffix && <span style={{ fontSize: big ? 14 : 11, color: 'var(--ink-3)', marginLeft: 4 }}>{suffix}</span>}
      </div>
      {hint && <div style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function AIInsightsSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {[0,1,2].map(i => (
        <div key={i} className="card" style={{ padding: 18, display: 'flex', gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--paper-3)', position: 'relative', overflow: 'hidden' }}>
            <div className="shimmer"/>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ width: '60%', height: 14, borderRadius: 4, background: 'var(--paper-3)', position: 'relative', overflow: 'hidden' }}>
              <div className="shimmer"/>
            </div>
            <div style={{ width: '90%', height: 10, borderRadius: 4, background: 'var(--paper-3)', marginTop: 8, position: 'relative', overflow: 'hidden' }}>
              <div className="shimmer"/>
            </div>
            <div style={{ width: '70%', height: 10, borderRadius: 4, background: 'var(--paper-3)', marginTop: 6, position: 'relative', overflow: 'hidden' }}>
              <div className="shimmer"/>
            </div>
          </div>
        </div>
      ))}
      <div style={{
        textAlign: 'center', marginTop: 4,
        fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ember)',
      }}>
        <Icon name="sparkle" size={11} color="var(--ember)"/> Lendo seus dados...
      </div>
    </div>
  );
}
